import { FC, FormEvent, useEffect, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import {
  CaixaSession,
  closeSession,
  fetchCaixaDashboard,
  fetchOpenSession,
} from "../utils/caixa.api";
import {
  formatMoney,
  formatCurrencyInput,
  parseCurrencyInput,
} from "../utils/caixa.utils";

const BRL_MONEY_PATTERN = /^\d{1,3}(\.\d{3})*,\d{2}$/;

function digitsToBrl(digits: string): string {
  const only = digits.replace(/\D/g, "");
  const cents = Number(only || "0");
  return formatCurrencyInput(cents / 100);
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    const msg = data?.message ?? data?.error;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  cancelLabel?: string;
}

export const FechamentoCaixaForm: FC<Props> = ({
  onSuccess,
  onCancel,
  cancelLabel = "Cancelar",
}) => {
  const [session, setSession] = useState<CaixaSession | null>(null);
  const [closingAmount, setClosingAmount] = useState("0,00");
  const [notes, setNotes] = useState("");
  const [expected, setExpected] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);

  useEffect(() => {
    setLoadingSession(true);
    Promise.all([fetchOpenSession(), fetchCaixaDashboard()])
      .then(([open, dash]) => {
        setSession(open);
        if (!open) return;
        const expectedCash =
          Number(open.opening_fund || 0) +
          Number(dash.total_sales || 0) +
          Number(dash.suprimentos_total || 0) -
          Number(dash.sangrias_total || 0);
        setExpected(expectedCash);
        setClosingAmount(formatCurrencyInput(expectedCash));
      })
      .catch((error) =>
        Swal.fire(
          "Erro",
          apiErrorMessage(error, "Não foi possível carregar a sessão."),
          "error",
        ),
      )
      .finally(() => setLoadingSession(false));
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session?._id) {
      Swal.fire("Atenção", "Não há caixa aberto para fechar.", "warning");
      return;
    }
    if (!BRL_MONEY_PATTERN.test(closingAmount.trim())) {
      Swal.fire(
        "Atenção",
        "Informe o valor contado no formato R$ (ex.: 300,00 ou 1.000,00).",
        "warning",
      );
      return;
    }
    const amount = parseCurrencyInput(closingAmount);
    setLoading(true);
    try {
      await closeSession(session._id, {
        closing_amount: amount,
        notes: notes || undefined,
      });
      await Swal.fire("Sucesso", "Caixa fechado com sucesso!", "success");
      window.dispatchEvent(new Event("caixa:session-changed"));
      onSuccess();
    } catch (error) {
      Swal.fire(
        "Erro",
        apiErrorMessage(error, "Não foi possível fechar o caixa."),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingSession) {
    return <p className="text-muted mb-0">Carregando sessão...</p>;
  }

  if (!session) {
    return <p className="text-muted mb-0">Nenhum caixa aberto no momento.</p>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-5 mb-5">
        <div className="col-md-4">
          <div className="stat-card">
            <div className="stat-label">Fundo Inicial</div>
            <div className="stat-value">
              {formatMoney(session.opening_fund)}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="stat-card">
            <div className="stat-label">Saldo Esperado</div>
            <div className="stat-value primary">{formatMoney(expected)}</div>
          </div>
        </div>
        <div className="col-md-4">
          <label
            className="form-label required"
            htmlFor="fechamento-valor-contado"
          >
            Valor Contado (R$)
          </label>
          <div className="input-group input-group-sm">
            <span className="input-group-text">R$</span>
            <input
              id="fechamento-valor-contado"
              type="text"
              inputMode="numeric"
              className="form-control form-control-sm"
              value={closingAmount}
              onChange={(e) => setClosingAmount(digitsToBrl(e.target.value))}
              onBlur={() => setClosingAmount(digitsToBrl(closingAmount))}
              required
            />
          </div>
        </div>
      </div>

      <div className="mb-5">
        <label className="form-label">Observações</label>
        <textarea
          className="form-control form-control-sm"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Diferenças, observações do fechamento..."
        />
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-light btn-sm me-3"
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
        <button
          type="submit"
          className="btn btn-danger btn-sm"
          disabled={loading}
        >
          {loading ? "Fechando..." : "Fechar Caixa"}
        </button>
      </div>
    </form>
  );
};
